import React from 'react';

import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { HierarchyFilesIcon } from 'src/components/icons/HierarchyFilesIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { makeStyles } from 'tss-react/mui';

import { AlineTooltip } from './AlineTooltip';

/**
 * A muted blue from the Figma design, distinct from `primary.main` and with no
 * equivalent token in `theme.ts`.
 */
const FIGMA_CHIP_BACKGROUND = 'rgba(87, 138, 208, 0.1)';

const useStyles = makeStyles()(theme => ({
  chip: {
    alignItems: 'center',
    backgroundColor: FIGMA_CHIP_BACKGROUND,
    borderRadius: 4,
    display: 'flex',
    flexShrink: 0,
    gap: 2,
    height: 20,
    padding: '3px 4px',
  },
  count: {
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1,
  },
}));

interface RelationshipCountChipProps {
  count: number;
  tooltip: string;
  dataCy?: string;
  isLoading?: boolean;
}

export const RelationshipCountChip = React.memo(function RelationshipCountChip({
  count,
  dataCy,
  isLoading = false,
  tooltip,
}: RelationshipCountChipProps): JSX.Element {
  const { classes } = useStyles();
  const theme = useTheme();

  return (
    <AlineTooltip arrow placement="top" title={tooltip}>
      <div className={classes.chip} data-cy={dataCy}>
        {isLoading ? (
          <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
        ) : (
          <Typography className={classes.count}>{count}</Typography>
        )}
        <HierarchyFilesIcon height={12} stroke={theme.palette.text.primary} width={12} />
      </div>
    </AlineTooltip>
  );
});
